/**
 * Finance — Tier changes.
 *
 *   "I just clicked 'talk to Orchid' on the provider billing screen, but i went
 *    to the Portal and found nothing waiting for me. it should probably go to
 *    the Support Centre, queued under Finance, the customer side of things,
 *    under Tier Changes - the only place where we first get customer requests
 *    for upgrades to Enterprise."
 *
 * The screen behind that routing. Script 138 sends an Enterprise enquiry here
 * with department = 'finance' and section = 'modify_subscription_tier', and
 * fn_tickets_on_my_desk lets whoever holds that permission read it without also
 * holding a Support Centre one — otherwise routing a ticket to Finance would
 * leave it somewhere Finance still cannot see.
 *
 * THE 48 HOURS IS A PROMISE SOMEBODY MADE ON ORCHID'S BEHALF
 *
 * fn_ask_about_enterprise tells the provider, in those words, that somebody
 * will be in touch inside two days. That sentence is the reason this screen
 * leads with how long a ticket has been waiting rather than with what it says:
 * a queue sorted by arrival is a queue you can work, and a promise nobody can
 * see the clock on is a promise that quietly expires.
 *
 * WHAT THIS SCREEN DOES NOT DO
 *
 * Change anybody's tier. Script 134 was explicit that an Enterprise price is
 * agreed rather than switched on, and nothing here touches service_providers.
 * The work this queue represents is a phone call; the outcome of that call is a
 * separate act by somebody who has had it.
 *
 * ---------------------------------------------------------------------------
 * AND WHAT IT COULD NOT DO UNTIL SCRIPT 360
 *
 * Anything at all. The founder shelved this as *"a tier request you can read
 * and cannot touch"*, which reads as a screen somebody forgot to put buttons
 * on. Driven on a gate database before a line was written — a desk holding
 * `modify_subscription_tier`, MFA-enrolled, against a ticket routed exactly as
 * `fn_ask_about_enterprise` routes one:
 *
 *   READ    fn_tickets_on_my_desk  -> 1 row
 *   ASSIGN  fn_assign_ticket       -> 42501
 *   MOVE    fn_move_ticket         -> 42501
 *
 * `fn_tickets_on_my_desk` gates per row on *"the person holds the permission
 * this ticket's section IS"* — the rule that makes this screen possible at all.
 * The two act-on functions never learned it: they asked for
 * `view_support_tickets` and then for the stream grant, both Support Centre
 * permissions this desk has no business holding. **Adding controls here without
 * that migration would have shipped two buttons that refuse on every press**,
 * and nothing could have seen it: the database suite never reads the screens
 * and Jest never reaches the database.
 *
 * Script 360 gives the act-on path the read path's own rule, pinned to the
 * customer stream so a section cannot reach an internal staff ticket.
 */

import { useCallback, useEffect, useState } from 'react';
import { O, Card, StatusPill, LoadingPanel, Notice } from '../ui';
import { fetchTicketsOnMyDesk } from '../lib/support';
import Move from './TicketMove';

/**
 * How long somebody has been waiting, against the promise they were given.
 *
 * Two days is the commitment, so the tone turns at two days rather than at some
 * number chosen here. A screen that went amber on its own schedule would be
 * measuring something other than what the customer was told.
 */
const waitTone = (days) => (days >= 2 ? 'critical' : days >= 1 ? 'warning' : 'quiet');
const waitLabel = (days) => (days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`);

const STATUS = {
  raised:      { label: 'Nobody has picked it up', tone: 'warning' },
  triaged:     { label: 'Triaged', tone: 'info' },
  assigned:    { label: 'Assigned', tone: 'info' },
  in_progress: { label: 'Being handled', tone: 'info' },
  reopened:    { label: 'Reopened', tone: 'warning' },
  resolved:    { label: 'Resolved', tone: 'green' },
};

function Ticket({ row, onChanged }) {
  const status = STATUS[row.status] || { label: row.status, tone: 'quiet' };

  return (
    <Card style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: O.ink, margin: 0 }}>
          {row.subject}
        </p>
        <StatusPill tone={status.tone}>{status.label}</StatusPill>
        <StatusPill tone={waitTone(row.waiting_days)}>
          {waitLabel(row.waiting_days)}
        </StatusPill>
      </div>

      {/* THE BODY IS THE TRIAGE. fn_ask_about_enterprise writes the business
          name, the plan they are on now and their own sentence into it, so the
          first person to open this is not starting from a name. */}
      <p style={{
        fontSize: 13, color: O.ink, margin: '10px 0 0', lineHeight: 1.7, whiteSpace: 'pre-wrap',
      }}>{row.body}</p>

      <p style={{ fontSize: 11, color: O.inkFaint, margin: '10px 0 0' }}>
        {row.reference}
        {row.raiser_ofx_code ? ` · ${row.raiser_ofx_code}` : ''}
        {row.origin ? ` · raised from ${row.origin}` : ''}
      </p>

      {/* THE CONTROL THAT COULD NOT EXIST UNTIL SCRIPT 360.
          Driven on a gate database before it was written: a desk holding
          `modify_subscription_tier` and neither Support Centre grant READ this
          queue and got 42501 from both fn_assign_ticket and fn_move_ticket.
          Adding buttons without the migration would have shipped two controls
          that refuse on every press.

          The same component the Support Centre uses, imported rather than
          copied, so the two desks cannot come to disagree about which moves
          are legal. */}
      <Move
        row={row}
        onDone={onChanged}
        hint="What was agreed on the call. This is what the provider is told, and what whoever picks this up next reads."
      />
    </Card>
  );
}

export default function TierChanges() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const { data, error: err } = await fetchTicketsOnMyDesk('finance', 'modify_subscription_tier');
    if (err) { setError(err); setRows([]); return; }
    setError(null);
    setRows(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (rows === null && !error) return <LoadingPanel message="Reading the tier queue…" />;

  return (
    <div>
      {error && <Notice tone="critical">{error}</Notice>}

      {(rows || []).length === 0 ? (
        <Card>
          <p style={{ fontSize: 13, color: O.inkMuted, margin: 0, lineHeight: 1.7 }}>
            Nothing is waiting. A provider pressing <strong>Talk to Orchid</strong> beside the
            Enterprise plan lands here, with what they are paying for today and anything they
            wrote — and they have been told somebody will be in touch inside 48 hours.
          </p>
        </Card>
      ) : (
        <>
          {/* Oldest first, which is fn_tickets_on_my_desk's own ordering and
              the founder's rule for every queue in this portal. Rendered in the
              order it is given rather than re-sorted, so there is one answer to
              "which has waited longest" and it is the database's. */}
          {(rows || []).map((row) => (
            <Ticket key={row.id} row={row} onChanged={load} />
          ))}

          <p style={{ fontSize: 11.5, color: O.inkFaint, margin: '4px 0 0', lineHeight: 1.6 }}>
            Nothing here changes a tier. An Enterprise price is agreed with the business, and the
            provider stays on the plan they are paying for until somebody has had that conversation.
            What this desk can do is take the request, work it, and record what was agreed.
          </p>
        </>
      )}
    </div>
  );
}
